import React from 'react'
import { Typography, Stack, Box } from '@mui/material'
import { ClockPickerView } from '@mui/x-date-pickers'
import { useFormContext, useWatch } from 'react-hook-form'

import { Moment } from '@e3dc-react/shell/Libs/moment'
import { isToday } from '@e3dc-react/shell/Libs/date'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TimePicker } from 'src/Libs/inputs/react-hook-form'
import { ChargingScheduleFormFieldNames, ChargingScheduleFormType } from 'src/Libs/e-mobility/vehicle-integration'

interface Props {
  handleDateTimeTransform: (date: Moment | null) => Date | null
  isEditing: boolean
}

/**
 * Schedule start time form part
 */
const ScheduleStartTime: React.FC<Props> = ({ handleDateTimeTransform, isEditing }) => {
  const { t } = useCustomTranslation()
  const { control } = useFormContext<ChargingScheduleFormType>()
  const startDate = useWatch({ control, name: ChargingScheduleFormFieldNames.StartDate })
  const startTime = useWatch({ control, name: ChargingScheduleFormFieldNames.StartTime })

  const handleTimeToDisable = (timeValue: number, clockType: ClockPickerView): boolean => {
    const now = new Date()

    // If editing an existing schedule, do not disable any time
    if (isEditing) {
      return false
    }

    // If startDate is not today, allow all times
    if (!startDate || !isToday(startDate)) {
      return false
    }

    // If clockType is 'hours', disable hours less than current hour
    if (clockType === 'hours') {
      return timeValue < now.getHours()
    }

    // If clockType is 'minutes', disable minutes less than current minute for current hour
    if (clockType === 'minutes' && startTime) {
      // If selected hour is current hour, disable minutes less than now
      if (startTime.getHours() === now.getHours()) {
        return timeValue < now.getMinutes()
      }
    }
    return false
  }

  return (
    <Stack gap={2} direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h3" sx={{ flex: 1 }}>
        {t('emobility.vehicle-integration.charging-schedule.start-time-label')}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <TimePicker
          name="startTime"
          textFieldStyles={{ width: '100%' }}
          shouldDisableTime={handleTimeToDisable}
          onTransform={handleDateTimeTransform}
        />
      </Box>
    </Stack>
  )
}

export default ScheduleStartTime
